import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PermissionSwitch from './partials/PermissionSwitch/PermissionSwitch';
import './permissionsSelector.css';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { rolesI18n } from './i18n';

const GROUP_ORDER = [
  'panel',
  'calendario',
  'estado diario',
  'tareas',
  'causas',
  'crm',
  'smart documents',
  'mensajería',
  'informes',
  'inteligencia artificial',
  'configuración',
];

const HIDDEN_BY_GROUP = {
  crm: {
    actions: ['cobranzas', 'agenda', 'gestionar automatizaciones'],
  },
};

const normalize = (str = '') =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const formatGroupLabel = (label = '') => {
  const normalized = label.trim().toLowerCase();

  if (normalized === 'crm') return 'CRM';

  return label.charAt(0).toUpperCase() + label.slice(1);
};

const sortGroups = (groups) => {
  return [...groups].sort((a, b) => {
    const aIndex = GROUP_ORDER.indexOf(normalize(a.label));
    const bIndex = GROUP_ORDER.indexOf(normalize(b.label));

    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });
};

const shouldRenderAction = (groupLabel, actionLabel) => {
  const groupKey = normalize(groupLabel);
  const actionKey = normalize(actionLabel);

  const rules = HIDDEN_BY_GROUP[groupKey];
  if (!rules) return true;

  if (rules.actions?.includes(actionKey)) return false;

  return true;
};

const toggleRecursive = (node, value) => {
  node.permission = value;
  node.children?.forEach((child) => toggleRecursive(child, value));
};

const getNodeState = (node) => {
  if (!node.children || node.children.length === 0) {
    return node.permission ? 'active' : 'inactive';
  }

  const states = node.children.map(getNodeState);

  if (states.every((state) => state === 'active')) return 'active';
  if (states.every((state) => state === 'inactive')) return 'inactive';
  return 'neutral';
};

const getGroupState = (group) => {
  const states = group.actions.map(getNodeState);

  if (states.every((state) => state === 'active')) return 'active';
  if (states.every((state) => state === 'inactive')) return 'inactive';
  return 'neutral';
};

const splitIntoColumns = (items, columns = 2) => {
  const result = Array.from({ length: columns }, () => []);
  items.forEach((item, index) => {
    result[index % columns].push(item);
  });
  return result;
};

const PermissionNode = ({
  node,
  onToggle,
  level = 1,
  isOpen,
  toggleOpen,
  t,
}) => {
  const state = getNodeState(node);

  const hasChildren = node.children?.length > 0;

  return (
    <div style={{ marginLeft: level * 16 }} className='permission-node mb-1'>
      <div
        className={[
          'd-flex align-items-center justify-content-between gap-2',
          hasChildren && isOpen(node.id) ? 'mb-2' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className='d-flex align-items-center mb-1'>
          <PermissionSwitch
            threeState
            value={state}
            onChange={(value) => onToggle(node, value === 'active')}
          />
          <p
            className='caption ms-2'
            style={{ cursor: hasChildren ? 'pointer' : 'default' }}
            onClick={() => hasChildren && toggleOpen(node.id)}
          >
            {node.label}
          </p>
        </div>
        {hasChildren && (
          <p
            className='caption font-blue ms-2'
            style={{ cursor: 'pointer' }}
            onClick={() => toggleOpen(node.id)}
          >
            {isOpen(node.id)
              ? t('roles_permissions_hide')
              : t('roles_permissions_show')}
          </p>
        )}
      </div>

      {hasChildren &&
        isOpen(node.id) &&
        node.children.map((child) => (
          <PermissionNode
            key={child.id}
            node={child}
            onToggle={onToggle}
            level={level + 1}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
            t={t}
          />
        ))}
    </div>
  );
};

const PermissionsSelector = ({ tree = [], onChange }) => {
  const { language } = useLanguage();
  const rolesLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: rolesI18n });

  const [openNodes, setOpenNodes] = useState(new Set());
  const orderedTree = sortGroups(tree);
  const columns = splitIntoColumns(orderedTree, 2);

  useEffect(() => {
    rolesI18n.changeLanguage(rolesLanguage);
  }, [rolesLanguage]);

  const toggleOpen = (id) => {
    setOpenNodes((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isOpen = (id) => openNodes.has(id);

  const handleToggleNode = (node, value) => {
    const next = structuredClone(tree);

    const walk = (nodes) => {
      for (const currentNode of nodes) {
        if (currentNode.id === node.id) {
          toggleRecursive(currentNode, value);
          return true;
        }
        if (currentNode.children && walk(currentNode.children)) return true;
      }
      return false;
    };

    next.forEach((group) => group.actions.forEach((action) => walk([action])));
    onChange(next);
  };

  const handleToggleGroup = (group, value) => {
    const next = structuredClone(tree);
    const selectedGroup = next.find((item) => item.id === group.id);
    selectedGroup.actions.forEach((action) => toggleRecursive(action, value));
    onChange(next);
  };

  if (!tree.length) {
    return (
      <p className='color-gray-600 text-center py-3'>
        {t('roles_permissions_empty')}
      </p>
    );
  }

  return (
    <div className='row'>
      {columns.map((column, colIndex) => (
        <div key={colIndex} className='col-6'>
          {column.map((group) => {
            const groupState = getGroupState(group);

            return (
              <div key={group.id} className='mb-4 border rounded-3 p-3'>
                <div
                  className={[
                    'd-flex align-items-center justify-content-between gap-2',
                    isOpen(group.id) ? 'mb-2' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className='d-flex align-items-center'>
                    <PermissionSwitch
                      threeState
                      value={groupState}
                      onChange={(value) =>
                        handleToggleGroup(group, value === 'active')
                      }
                    />
                    <p
                      className='fw-medium mb-0 ms-2'
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleOpen(group.id)}
                    >
                      {formatGroupLabel(group.label)}
                    </p>
                  </div>
                  <p
                    className='caption font-blue'
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleOpen(group.id)}
                  >
                    {isOpen(group.id)
                      ? t('roles_permissions_hide')
                      : t('roles_permissions_show')}
                  </p>
                </div>

                {isOpen(group.id) &&
                  group.actions
                    .filter((action) =>
                      shouldRenderAction(group.label, action.label)
                    )
                    .map((action) => (
                      <PermissionNode
                        key={action.id}
                        node={action}
                        onToggle={handleToggleNode}
                        isOpen={isOpen}
                        toggleOpen={toggleOpen}
                        t={t}
                      />
                    ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default PermissionsSelector;
